// The player screen (#/trainer): everything about you in one place, opened
// from the player card above the team. The left column is always you (look,
// name, guild tag, level, boosts); the tabs on the right hold the rest:
// Overview (lifetime totals, records, guild, friends), Outfits, Friends and
// Settings. Each tab fits one screen at 1440x900, with no scrolling.
import { psecTabs, psecWrap } from './psec.ts';

// Phone sections of the Trainer sub-tabs (client/psec.ts): one at a time on a phone, all in place elsewhere.
const CARD_SECS = ['card', 'show', 'picks'];
const OUTFIT_SECS = ['look', 'outfits'];
const FR_SECS = ['list', 'add'];
const SET_SECS = ['game', 'sound', 'acct', 'stats'];
import { CONFIG } from '../shared/config.ts';
import { SPECIES, DEX_SPECIES } from '../shared/species.ts';
import { trainerXpToNext } from '../shared/xp.ts';
import { GUILD_BOOST_MULT } from '../shared/guilds.ts';
import { PASS, passActive, nameColorOf } from '../shared/pass.ts';
import { OUTFITS, ownsOutfit, outfitHow } from '../shared/outfits.ts';
import { SOCIAL, GIFT, giftAmount, cleanCardPrefs } from '../shared/social.ts';
import { wearableTitles } from '../shared/titles.ts';
import { BADGE } from '../shared/pass.ts';
import { badgePic, wearTitle } from './pass.ts';
import { openDialog } from './dialog.ts';
import { ratingOf } from '../shared/rating.ts';
import { CATCH_SOUNDS, type CatchSound } from '../shared/golden.ts';
import type { CompanionRef, CreatureDTO } from '../shared/protocol.ts';
import { withSkin } from '../shared/skins.ts';
import { S, serverNow } from './state.ts';
import { spriteUrl, uiIcon } from './assets.ts';
import { esc, full, num, left, duration, cname, nameHtml } from './fmt.ts';
import { route } from './router.ts';
import { shownOutfit, outfitCards } from './outfits.ts';
import { F, playerLink, giftCalcText, giftConfirmKey, sendShowcase, sendCompanion, sendCard } from './social.ts';
import { playerIcon } from './playericon.ts';
import { creatureIcon } from './assets.ts';
import { creatureGrade } from './badges.ts';
import { G } from './guild.ts';
import { trainerFace } from './outfits.ts';
import { rollPills } from './rollpills.ts';
import { isArmed } from './confirm.ts';
import { batterySaver, setBatterySaver, getZoom, setZoom } from './field.ts';
import { showWayfarers, setShowWayfarers } from './wayfarers.ts';
import { ICON } from './icons.ts';
import { auraOneInAt, charmUntil } from '../shared/auracharm.ts';
import { catchCharmEffect, catchCharmUntil } from '../shared/catchcharm.ts';
import { frameOf, ownedFrames } from '../shared/frames.ts';
import { PAID_BOOST, PAID_BOOST_KINDS, paidUntil, multX, pctX } from '../shared/boosts.ts';
import { lateJoinerOn } from '../shared/latejoiner.ts';
import { EVENT_KINDS } from '../shared/events.ts';
import { GROUND, REGIONS } from '../shared/grounds.ts';
import { eventGroups } from './events.ts';
import { privatePanel, accountPanel, accountClick, playTime } from './account-settings.ts';
import { dmFromSelect } from './messages.ts';
import { streamBoost, streamBoostX } from './streams.ts';
import { friendList } from './friends-list.ts';
import { lookPicker, lookDraft } from './lookpicker.ts';

export const HUB_TABS = [['', 'Overview'], ['showcase', 'Card'], ['outfits', 'Outfits'], ['friends', 'Friends'], ['settings', 'Settings']] as const;
const TAB_ICON: Record<(typeof HUB_TABS)[number][0], string> = { '': 'ui-overview', showcase: 'ui-showcase', outfits: 'ui-outfits', friends: 'ui-friends', settings: 'ui-gear' };
const tabOf = () => { const s = route().sub; return HUB_TABS.some(([id]) => id === s) ? s : ''; };

/** Next level at this session's rate, or the lead's estimate here; given by ui.ts, which owns both. */
let rate: () => { rate: number; measured: boolean } | null = () => null;
export function bindHub(o: { rate: typeof rate }) { rate = o.rate; }

export function hubHead() {
  const t = S.trainer!;
  const waiting = F.data ? F.data.incoming.length + F.data.friends.reduce((a, f) => a + f.unread, 0) : 0;
  const cur = tabOf();
  return {
    meta: `Lv ${full(t.level)}${t.guild ? ` · ${esc(t.guild.name)}` : ''}`,
    tabs: `<nav class="subtabs">${HUB_TABS.map(([id, n]) => `<a href="#/trainer${id ? `/${id}` : ''}" class="${cur === id ? 'on' : ''}">${id === 'showcase' ? uiIcon(TAB_ICON[id], `<span class="ti hub-ti hub-svg">${ICON.creatures}</span>`, 'ti hub-ti', 24) : uiIcon(TAB_ICON[id], '', 'ti hub-ti', 24)} ${n}${id === 'friends' && waiting ? ` <span class="unread">${waiting}</span>` : ''}</a>`).join('')}</nav>`,
  };
}

// ---------------- the identity column ----------------
function identity() {
  const t = S.trainer!;
  const need = trainerXpToNext(t.level);
  const frac = Math.max(0, Math.min(1, t.xp / need));
  const r = rate();
  const now = serverNow();
  const outfit = shownOutfit();
  const previewing = outfit.id !== (t.outfit ?? 'traveler');
  const boosts = [
    // One notation for every line: the source as a short label, the effect as "×1.5 XP".
    passActive(t, now) ? ['Pass', multX(PASS.xpMult), left((t.pass?.until ?? 0) - now)] : null,
    t.boosts.vipUntil > now ? ['VIP', multX(CONFIG.vipXpMult), left(t.boosts.vipUntil - now)] : null,
    t.boosts.xpUntil > now ? ['Boost', multX(CONFIG.xpBoostMult), left(t.boosts.xpUntil - now)] : null,
    t.boosts.coinUntil > now ? ['Boost', multX(CONFIG.coinBoostMult, 'coins'), left(t.boosts.coinUntil - now)] : null,
    charmUntil(t.boosts, now) ? ['Aura Charm', `1 in ${full(auraOneInAt(t.boosts, now))} catches`, left(charmUntil(t.boosts, now) - now)] : null,
    catchCharmUntil(t.boosts, now) ? ['Catch Charm', catchCharmEffect(), left(catchCharmUntil(t.boosts, now) - now)] : null,
    t.guild && t.guild.boostUntil > now ? ['Guild', multX(GUILD_BOOST_MULT), left(t.guild.boostUntil - now)] : null,
    t.guild?.war && t.guild.war.until > now ? ['Guild War', pctX(t.guild.war.pct, 'XP and loot'), left(t.guild.war.until - now)] : null,
    streamBoost() ? ['Streamer', streamBoostX(), 'watching'] : null,
    t.lateJoiner && lateJoinerOn(t) ? ['Late Joiner', multX(CONFIG.lateJoiner.mult), `to Lv ${full(t.lateJoiner.target)}`] : null,
    ...PAID_BOOST_KINDS.filter((k) => paidUntil(t.boosts, k) > now).map((k) => ['Boost', PAID_BOOST[k].effect, left(paidUntil(t.boosts, k) - now)]),
    ...eventGroups(now, GROUND[t.ground]?.region ?? '').filter((g) => g.here).map((g) => [`Event${g.region ? ` · ${REGIONS.find((x) => x.id === g.region)?.name ?? g.region}` : ''}`, `×${+g.mult.toFixed(2)} ${EVENT_KINDS[g.kind].name}`, left(g.end - now)]),
  ].filter((x): x is string[] => !!x);
  return `<section class="panel ph-id">
    <div class="ph-portrait">${playerIcon({ outfit: outfit.id, look: t.look, comp: myCompanion(), size: 'xl', name: `${t.name} in the ${outfit.name}` })}${previewing ? `<span class="ph-preview">Preview${ownsOutfit(t, outfit.id) ? '' : ` · ${esc(outfitHow(outfit))}`}</span>` : ''}</div>
    <h2 class="ph-name">${t.guild ? `<span class="gtag">[${esc(t.guild.tag)}]</span> ` : ''}<span style="color:${nameColorOf(t, now) ?? 'inherit'}">${esc(t.name)}</span></h2>${t.title ? `<div class="trainer-title">${esc(t.title)}</div>` : ''}
    <div class="ph-outfit">${esc(outfit.name)} · <a href="#/trainer/outfits">Change</a></div>
    <div class="ph-lv"><span>Trainer</span><b>Lv ${full(t.level)}</b><span class="muted">${Math.floor(frac * 100)}%</span></div>
    <div class="bar xp big"><i style="width:${(frac * 100).toFixed(1)}%"></i><span>XP<em>${full(t.xp)} / ${full(need)}</em></span></div>
    ${r ? `<p class="ph-next">Lv ${t.level + 1} in ${duration(((need - t.xp) / r.rate) * 3_600_000)} · ${num(r.rate)} XP/h</p>` : ''}
    <div class="ph-boosts"><div class="ph-sub">Boosts</div>${boosts.length ? `<ul>${boosts.map(([n, e, l]) => `<li><b>${n}</b><span>${e}</span><span class="muted">${l}</span></li>`).join('')}</ul>` : '<p class="muted">—</p>'}<a class="link" href="#/shop/diamonds">Diamond shop</a></div>
  </section>`;
}

// ---------------- overview ----------------
const tile = (label: string, value: string, note = '') => `<div class="ph-tile"><span>${label}</span><b>${value}</b>${note ? `<em>${note}</em>` : ''}</div>`;

const HUB_SECS = ['you', 'life', 'records', 'social'];
function overview() {
  const t = S.trainer!;
  const all = [...S.creatures.values()];
  const caughtSpecies = Object.values(t.dex).filter((d) => d.caught > 0).length;
  const st = t.stats;
  const lifetime = `<section class="panel ph-life"><h4>Lifetime</h4><div class="ph-tiles">
    ${tile('Defeated', full(st.kills))}${tile('Caught', full(st.catches))}${tile('Auras caught', full(st.auras))}
    ${tile('Time played', st.playMs ? playTime(st.playMs) : '—')}
    ${tile('Creatures', full(all.length))}${tile('Bestiary', `${caughtSpecies} / ${DEX_SPECIES.length}`)}
    ${tile('Daily streak', `${t.daily.streak} ${t.daily.streak === 1 ? 'day' : 'days'}`)}${tile('Favorites', full((t.collection ?? []).length))}</div></section>`;

  // Records: the best of what you own and have done.
  const best = all.reduce((b: CreatureDTO | null, c) => (!b || ratingOf(c.rolls, c.species) > ratingOf(b.rolls, b.species) ? c : b), null);
  const top = all.reduce((b: CreatureDTO | null, c) => (!b || c.level > b.level ? c : b), null);
  const auras = all.filter((c) => c.rolls.aura).length;
  const recRow = (label: string, body: string) => `<div class="ph-rec"><span>${label}</span><div>${body}</div></div>`;
  const records = `<section class="panel ph-records"><h4>Records</h4>
    ${best ? recRow('Best catch', `<span class="cn" data-open="${best.id}">${nameHtml(best)} <span class="muted">Lv ${best.level}</span></span>${rollPills(best, { rollsOnly: true })}`) : ''}
    ${top ? recRow('Highest level', `<span class="cn" data-open="${top.id}">${nameHtml(top)}</span> <b>Lv ${top.level}</b>`) : ''}
    ${recRow('Auras owned', `<b>${full(auras)}</b>`)}
    ${recRow('Boss Ladder', t.ladder?.best ? `best step <b>${full(t.ladder.best)}</b> · ${full(t.ladder.wins)} wins` : '—')}
    ${recRow('Tournaments', `<a class="link" href="#/tournaments"><b>${full(t.trophies?.length ?? 0)}</b> ${(t.trophies?.length ?? 0) === 1 ? 'trophy' : 'trophies'}</a> · ${full(t.tickets ?? 0)} ${(t.tickets ?? 0) === 1 ? 'ticket' : 'tickets'}`)}
    ${t.halls?.length ? recRow('Halls kept', `<b>${t.halls.length}</b>`) : ''}
    <div class="ph-links"><a class="link" href="#/records">Server records</a><a class="link" href="#/leaderboards">Leaderboards</a></div></section>`;

  // Guild: a summary and the way in.
  const gv = G.view?.guild;
  const now = serverNow();
  const guild = t.guild
    ? `<section class="panel ph-guild"><h4>Guild</h4>
      <div class="ph-gname">${ICON.guild}<div><b>${esc(t.guild.name)}</b> <span class="gtag">[${esc(t.guild.tag)}]</span><div class="muted">${t.guild.role === 'owner' ? 'Owner' : t.guild.role === 'coowner' ? 'Co-owner' : 'Member'}${gv ? ` · ${gv.members.length} members · ${gv.members.filter((m) => m.online).length} online` : ''}</div></div></div>
      ${t.guild.boostUntil > now || (t.guild.war && t.guild.war.until > now) ? `<p class="muted">${t.guild.boostUntil > now ? `Guild ${multX(GUILD_BOOST_MULT)} · ${left(t.guild.boostUntil - now)}` : ''}${t.guild.war && t.guild.war.until > now ? `${t.guild.boostUntil > now ? ' · ' : ''}Guild War ${pctX(t.guild.war.pct, 'XP and loot')}` : ''}</p>` : ''}
      <a class="btn small" href="#/guild">Open guild</a></section>`
    : `<section class="panel ph-guild"><h4>Guild</h4><a class="btn small" href="#/guild">Find a guild</a></section>`;

  // Friends: who is around, and what waits for you.
  const d = F.data;
  const friends = d ? [...d.friends].sort((a, b) => Number(b.online) - Number(a.online) || a.name.localeCompare(b.name)) : [];
  const fr = `<section class="panel ph-friends"><div class="ph-h"><h4>Friends</h4><span class="muted">${d ? `${friends.filter((f) => f.online).length} online · ${friends.length} / ${SOCIAL.friendsMax}` : ''}</span></div>
    ${d ? (friends.length ? `<ul class="ph-flist" data-fit="ph-flist">${friends.slice(0, 4).map((f) => `<li data-key="f-${esc(f.name)}"><i class="gdot${f.online ? '' : ' off'}"></i>${trainerFace(f.outfit)}${playerLink(f.name, `<b>${esc(f.name)}</b>`)}<span class="muted">Lv ${full(f.level)}</span><a class="link" href="#/chat/dm/${encodeURIComponent(f.name)}">Message${f.unread ? ` (${f.unread})` : ''}</a></li>`).join('')}</ul>` : '<p class="muted">No friends yet.</p>') : '<p class="muted">Loading…</p>'}
    ${d?.incoming.length ? `<p class="ph-wait">${d.incoming.length} friend ${d.incoming.length === 1 ? 'request' : 'requests'} waiting · <a href="#/trainer/friends">Answer</a></p>` : ''}
    <a class="link" href="#/trainer/friends">${friends.length > 4 ? `All ${friends.length} friends` : 'Manage friends'}</a></section>`;
  return `<div class="ph-over">${psecWrap('hub', 'life', HUB_SECS, lifetime)}${psecWrap('hub', 'records', HUB_SECS, records)}${psecWrap('hub', 'social', HUB_SECS, guild + fr)}</div>`;
}

// ---------------- showcase and companion ----------------
/** Typed filter for the Showcase tab's creature list. */
let showFilter = '';
export function setShowFilter(v: string) { showFilter = v; }
const SHOW_LIST_MAX = 60;

/** Your companion as the icon shows it (your lead when none is set), while you still own it. */
export function myCompanion(): CompanionRef | null {
  const id = S.trainer?.companion ?? S.trainer?.team[0];
  const c = id !== undefined ? S.creatures.get(id) : undefined;
  return c ? withSkin({ species: c.species, aura: c.rolls.aura }, S.trainer ?? undefined) : null;
}

function showcaseTab() {
  const t = S.trainer!;
  const shown = (t.showcase ?? []).filter((id) => S.creatures.has(id));
  const compId = t.companion !== undefined && S.creatures.has(t.companion) ? t.companion : 0;
  const comp = compId ? S.creatures.get(compId)! : null;
  const slots = Array.from({ length: SOCIAL.showcaseMax }, (_, i) => {
    const c = S.creatures.get(shown[i] ?? -1);
    return c
      ? `<div class="ph-slot on" data-key="ss-${c.id}"><span class="ph-slot-pic" data-open="${c.id}">${creatureIcon(c.species, c.rolls.aura)}</span><b>${esc(cname(c))}</b><span class="ph-slot-sub">${creatureGrade(c, 'sm')} Lv ${full(c.level)}</span>
        <span class="ph-slot-acts"><button class="btn tiny" data-act="ph-move" data-id="${c.id}:-1" aria-label="Move ${esc(cname(c))} left"${i === 0 ? ' disabled' : ''}>‹</button><button class="btn tiny" data-act="ph-show" data-id="${c.id}" aria-label="Take ${esc(cname(c))} off the showcase">Remove</button><button class="btn tiny" data-act="ph-move" data-id="${c.id}:1" aria-label="Move ${esc(cname(c))} right"${i === shown.length - 1 ? ' disabled' : ''}>›</button></span></div>`
      : `<div class="ph-slot" data-key="ss-e${i}"><span class="ph-slot-n">${i + 1}</span></div>`;
  }).join('');
  const q = showFilter.trim().toLowerCase();
  const all = [...S.creatures.values()]
    .filter((c) => !q || cname(c).toLowerCase().includes(q) || (SPECIES[c.species]?.name ?? '').toLowerCase().includes(q) || (SPECIES[c.species]?.elements ?? []).some((e) => e.includes(q)))
    .sort((a, b) => ratingOf(b.rolls, b.species) - ratingOf(a.rolls, a.species) || b.level - a.level || a.id - b.id);
  const full6 = shown.length >= SOCIAL.showcaseMax;
  const list = all.slice(0, SHOW_LIST_MAX).map((c) => {
    const on = shown.includes(c.id), isComp = c.id === compId;
    return `<li class="ph-pick${on ? ' on' : ''}${isComp ? ' comp' : ''}" data-key="sp-${c.id}"><span class="ph-pick-pic" data-open="${c.id}">${creatureIcon(c.species, c.rolls.aura)}</span>
      <span class="ph-pick-nm"><b>${esc(cname(c))}</b>${creatureGrade(c, 'sm')}<span class="ph-pick-lv">Lv ${full(c.level)}</span></span>
      <button class="btn tiny${on ? ' on' : ''}" data-act="ph-show" data-id="${c.id}" aria-pressed="${on}"${!on && full6 ? ` disabled title="Showcase full (${SOCIAL.showcaseMax})"` : ''}>${on ? 'Shown' : 'Show'}</button>
      <button class="btn tiny${isComp ? ' on' : ''}" data-act="ph-comp" data-id="${isComp ? 0 : c.id}" aria-pressed="${isComp}">${isComp ? 'Companion' : 'Walk with'}</button></li>`;
  }).join('');
  const cs = (key: string, html: string) => psecWrap('card', key, CARD_SECS, html);
  return `${psecTabs('card', [{ key: 'card', title: 'Card' }, { key: 'show', title: 'Showcase' }, { key: 'picks', title: 'Creatures' }])}<div class="ph-show">${cs('card', `<div class="ph-col">
    <section class="panel ph-comp"><div class="ph-h"><h4>Companion</h4>${comp ? '<button class="btn tiny" data-act="ph-comp" data-id="0">Clear</button>' : ''}</div>
      <div class="ph-comp-row">${playerIcon({ outfit: t.outfit, look: t.look, comp: myCompanion(), size: 'xl' })}
        ${comp ? `<div><b class="cn" data-open="${comp.id}">${esc(cname(comp))}</b> ${creatureGrade(comp, 'sm')}<div class="muted">Lv ${full(comp.level)}</div></div>` : '<p class="muted">Your lead</p>'}</div></section>
    ${cardLook()}</div>`)}
    ${cs('show', `<section class="panel ph-slots-p"><div class="ph-h"><h4>Showcase</h4><span class="muted">${shown.length} / ${SOCIAL.showcaseMax}</span><a class="link" href="#/player/${encodeURIComponent(t.name)}" data-player="${esc(t.name)}">Preview</a></div>
      <div class="ph-slots">${slots}</div></section>`)}
    ${cs('picks', `<section class="panel ph-picks"><div class="ph-h"><h4>Your creatures</h4><label class="fsearch">${ICON.search}<input data-ph-filter placeholder="Name, species or element" value="${esc(showFilter)}" aria-label="Filter creatures"></label></div>
      <ul class="ph-picklist" data-fit="ph-picks">${list || '<li class="muted">No match.</li>'}</ul>${all.length > SHOW_LIST_MAX ? `<p class="muted ph-picks-n">Best ${SHOW_LIST_MAX} of ${full(all.length)} by grade.</p>` : ''}</section>`)}
  </div>`;
}

/**
 * The card frame (shared/frames.ts): every frame owned, pass and ranked alike. Shown once a ranked frame is owned;
 * until then the pass frame simply comes with the pass, as before.
 */
function frameRow(row: (label: string, control: string) => string, pick: (on: boolean, act: string, id: string, label: string) => string): string {
  const t = S.trainer!, now = serverNow();
  if (!(t.frames ?? []).length) return '';
  const cur = frameOf(t, now);
  return row('Frame', `<span class="ph-seg wrap">${pick(cur === null, 'pass-frame', 'none', 'None')}${ownedFrames(t, now).map((f) => pick(cur === f.id, 'pass-frame', f.id, esc(f.name))).join('')}</span>`);
}

/** The worn title as one button (its tip says how it was earned); it opens the picker. Titles pile up month on month, so they never list inline. */
function titleButton(titles: { title: string; how: string }[]) {
  const worn = titles.find((x) => x.title === S.trainer!.title);
  return `<button class="btn small ph-title" data-act="ph-titles"${worn?.how ? ` data-tip="${esc(worn.how)}"` : ''} aria-haspopup="dialog"><span>${worn ? esc(worn.title) : 'None'}</span><i aria-hidden="true">▾</i></button>`;
}

/** Every title earned, biggest first, each with how it was earned; a pick wears it at once. */
async function pickTitle() {
  const t = S.trainer!, titles = wearableTitles(t);
  const card = (value: string, name: string, how: string, on: boolean) =>
    `<button class="pcard tt-card${on ? ' on' : ''}" data-dlg="${esc(value)}" aria-pressed="${on}"><span class="grow"><b>${esc(name)}</b>${how ? `<span class="muted">${esc(how)}</span>` : ''}</span></button>`;
  const body = `<div class="picker tt-pick" data-fit="dlg-titles">${titles.map((x) => card(x.title, x.title, x.how, t.title === x.title)).join('')}${card(NO_TITLE, 'None', '', !t.title)}</div>`;
  const r = await openDialog({ title: `Titles · ${titles.length}`, body, actions: [] });
  if (r.value === null || (r.value === NO_TITLE ? !t.title : r.value === t.title)) return;
  wearTitle(r.value === NO_TITLE ? null : r.value);
}
/** The picker's "None" (an empty data-dlg would read as Cancel). */
const NO_TITLE = '\u0000none';

/** Title, chat badge and what the card keeps private. */
function cardLook() {
  const t = S.trainer!;
  const titles = wearableTitles(t);
  const badges = t.pass?.badges ?? [];
  const b = t.pass?.badge ?? null;
  const hide = t.card ?? {};
  const pick = (on: boolean, act: string, id: string, label: string) => `<button class="${on ? 'on' : ''}" data-act="${act}" data-id="${esc(id)}" aria-pressed="${on}">${label}</button>`;
  const row = (label: string, control: string) => `<div class="ph-set"><div><b>${label}</b></div><div class="ph-ctl">${control}</div></div>`;
  return `<section class="panel ph-look"><h4>Card</h4>
    ${row('Title', titles.length ? titleButton(titles) : '<span class="muted">—</span>')}
    ${frameRow(row, pick)}
    ${badges.length ? row('Chat badge', `<span class="ph-seg wrap">${pick(!b, 'pass-badge', '', 'None')}${badges.map((x) => pick(b === x, 'pass-badge', x, `${badgePic(x)} ${esc(BADGE[x]?.name ?? x)}`)).join('')}</span>`) : ''}
    ${row('Show Auras and top grade', `<input type="checkbox" class="toggle" data-act="ph-card" data-id="rarities"${hide.rarities ? '' : ' checked'} aria-label="Show Auras and top grade">`)}
    ${row('Show coins', `<input type="checkbox" class="toggle" data-act="ph-card" data-id="coins"${hide.coins ? '' : ' checked'} aria-label="Show coins">`)}
  </section>`;
}

// ---------------- outfits ----------------
function outfits() {
  const owned = OUTFITS.filter((x) => ownsOutfit(S.trainer!, x.id)).length;
  const o = shownOutfit();
  return `${psecTabs('outfits', [{ key: 'look', title: 'Look' }, { key: 'outfits', title: 'Outfits' }])}<section class="panel ph-outfits"><div class="ph-h"><h4>Outfits</h4><span class="muted">${owned} of ${OUTFITS.length} owned</span></div>
    ${psecWrap('outfits', 'look', OUTFIT_SECS, lookPicker(lookDraft({ outfit: o.id }), { outfits: false, live: true }))}
    ${psecWrap('outfits', 'outfits', OUTFIT_SECS, `<div class="ofgrid" data-fit="outfits">${outfitCards()}</div>`)}</section>`;
}

// ---------------- friends ----------------
function friendsTab() {
  const d = F.data;
  if (!d) return '<section class="panel"><p class="muted">Loading…</p></section>';
  const now = serverNow();
  const giftRows = (f: { name: string }) => `${F.giftTo.toLowerCase() === f.name.toLowerCase() ? giftForm(f) : ''}${F.giftDone && F.giftDone.until > Date.now() && F.giftDone.name.toLowerCase() === f.name.toLowerCase() ? `<li class="cg-row cg-done" data-key="gd-${esc(f.name)}">${esc(F.giftDone.text)}</li>` : ''}`;
  const list = friendList(d, { extra: (f) => giftButton(f, now), after: giftRows, fit: 'ph-friends' });
  const reqs = d.incoming.map((x) => `<li data-key="r${x.id}">${trainerFace(x.outfit)}${playerLink(x.name, `<b>${esc(x.name)}</b>`)}<span class="muted">Lv ${full(x.level)}</span><span class="spacer"></span><button class="btn tiny primary" data-act="fr-accept" data-id="${x.id}">Accept</button><button class="btn tiny" data-act="fr-decline" data-id="${x.id}">Decline</button></li>`).join('');
  const inv = d.invites.filter((i) => !i.mine).map((i) => `<li data-key="i${i.id}">${playerLink(i.from)} challenges you <span class="muted">· ${left(i.expires - now)}</span><span class="spacer"></span><button class="btn tiny primary" data-act="fr-inv-yes" data-id="${i.id}">Fight</button><button class="btn tiny" data-act="fr-inv-no" data-id="${i.id}">Decline</button></li>`).join('');
  const waiting = d.incoming.length + d.invites.filter((i) => !i.mine).length;
  return `${psecTabs('fr', [{ key: 'list', title: 'Friends' }, { key: 'add', title: 'Add and requests', dot: waiting > 0 }])}<div class="ph-fr">
    ${psecWrap('fr', 'list', FR_SECS, list)}
    ${psecWrap('fr', 'add', FR_SECS, `<div class="ph-frside">
      <section class="panel"><h4>Add a friend</h4><form class="fr-add" data-fr-form="add"><label class="sr" for="ph-add-name">Trainer name</label><input id="ph-add-name" name="name" data-fr="add" maxlength="16" placeholder="Trainer name" value="${esc(F.addName)}" autocomplete="off"><button class="btn small primary">Send request</button></form></section>
      <section class="panel"><h4>Waiting for you</h4>${reqs || inv ? `<ul class="ph-flist" data-fit="ph-wait">${inv}${reqs}</ul>` : '<p class="muted">—</p>'}${d.outgoing.length ? `<p class="muted">${d.outgoing.length} sent</p>` : ''}</section>
      <section class="panel ph-more"><h4>More</h4><a class="link" href="#/friends/battles">Friendly battles</a><a class="link" href="#/friends/training">Training</a><a class="link" href="#/friends/blocked">Blocked and muted</a></section>
    </div>`)}</div>`;
}

// ---------------- coin gifts ----------------
/** The viewer is an admin (given to hubPane): admins can't send coins. */
let viewerAdmin = false;
/** Coins you can send: admin-granted coins stay on the account. */
const giftable = () => { const t = S.trainer!; return Math.max(0, t.coins - Math.min(t.devCoins ?? 0, t.coins)); };

/** Why Send coins is off for this friend, or '' when it works. The server checks all of this again. */
function giftBlock(f: { name: string; level: number; giftAt: number }, now: number): string {
  const d = F.data!;
  if (viewerAdmin) return 'Admin accounts can\'t send coins';
  if (S.trainer!.level < GIFT.minLevel) return `Coin gifts open at Lv ${GIFT.minLevel}`;
  if (f.level < GIFT.minLevel) return `${f.name} can receive coins from Lv ${GIFT.minLevel}`;
  if (f.giftAt > now) return `Friends for ${GIFT.friendMs / 86_400_000} days first: ${left(f.giftAt - now)} to go`;
  if (d.gift.cap - d.gift.sent < GIFT.minCoins) return 'Daily gift limit reached (resets 00:00 UTC)';
  if (giftable() < GIFT.minCoins) return `You need at least ${full(GIFT.minCoins)} coins`;
  return '';
}

function giftButton(f: { name: string; level: number; giftAt: number }, now: number) {
  const why = giftBlock(f, now);
  const open = F.giftTo.toLowerCase() === f.name.toLowerCase();
  return `<button class="btn tiny${open ? ' on' : ''}" data-act="fr-gift" data-id="${esc(f.name)}"${why ? ` disabled title="${esc(why)}"` : ` title="Send ${esc(f.name)} coins"`} aria-expanded="${open}">Send coins</button>`;
}

function giftForm(f: { name: string }) {
  const d = F.data!;
  const max = Math.max(0, Math.min(giftable(), d.gift.cap - d.gift.sent));
  const amt = giftAmount(F.giftAmt.replace(/[,\s]/g, ''));
  const armed = amt !== null && isArmed(giftConfirmKey(f.name, amt));
  return `<li class="cg-row" data-key="g-${esc(f.name)}"><form class="cg-form" data-fr-form="gift" data-id="${esc(f.name)}">
    <label for="cg-amt">Coins to ${esc(f.name)}</label>
    <input id="cg-amt" name="amount" data-fr="gift" inputmode="numeric" pattern="[0-9]*" maxlength="16" placeholder="${full(GIFT.minCoins)}+" value="${esc(F.giftAmt)}" autocomplete="off">
    <button type="button" class="btn tiny" data-act="fr-gift-max" data-id="${max}" title="The most you can send now">Max ${full(max)}</button>
    <button class="btn tiny primary${armed ? ' armed' : ''}">${armed ? 'Confirm' : 'Send'}</button><button type="button" class="btn tiny" data-act="fr-gift">Cancel</button>
    <output class="cg-calc" for="cg-amt">${esc(giftCalcText(F.giftAmt))}</output></form></li>`;
}

// ---------------- settings ----------------
function settings(admin: boolean) {
  const z = getZoom();
  let theme = 'codex';
  try { theme = localStorage.getItem('mi-theme') === 'default' ? 'default' : 'codex'; } catch { /* storage off */ }
  const row = (label: string, control: string) => `<div class="ph-set"><div><b>${label}</b></div><div class="ph-ctl">${control}</div></div>`;
  const dev = admin
    ? row('Admin', `<span class="ph-seg" title="Theme (dev)">${[['codex', 'Codex'], ['default', 'Plain']].map(([v, n]) => `<button class="${theme === v ? 'on' : ''}" data-act="ph-theme" data-id="${v}">${n}</button>`).join('')}</span><a class="btn small" href="#/admin">Open</a>`)
    : '';
  const ss = (key: string, html: string) => psecWrap('set', key, SET_SECS, html);
  return `${psecTabs('set', [{ key: 'game', title: 'Game' }, { key: 'sound', title: 'Sounds' }, { key: 'acct', title: 'Account' }, { key: 'stats', title: 'Stats' }])}<div class="ph-settings"><div class="ph-col">
    ${ss('stats', privatePanel())}
    ${ss('game', `<section class="panel"><h4>Messages</h4>
      ${row('Who can message you', dmFromSelect())}
      ${row('Blocked and muted', `<span class="muted ph-n">${F.data?.blocked.length ?? 0}</span><a class="btn small" href="#/friends/blocked">Open</a>`)}
    </section>
    <section class="panel"><h4>Hunt screen</h4>
      ${row('Battery saver', `<input type="checkbox" class="toggle" data-act="ph-saver"${batterySaver() ? ' checked' : ''} aria-label="Battery saver">`)}
      ${row('Other trainers', `<input type="checkbox" class="toggle" data-act="ph-wayfarers"${showWayfarers() ? ' checked' : ''} aria-label="Show other trainers on the hunt">`)}
      ${row('Map zoom', `<span class="ph-seg" role="group" aria-label="Map zoom">${[1, 2, 3, 4].map((v) => `<button class="${z === v ? 'on' : ''}" data-act="ph-zoom" data-id="${v}" aria-pressed="${z === v}">${v}×</button>`).join('')}</span>`)}
      ${row('Automation', '<a class="btn small" href="#/hunt">Open</a>')}
    </section>`)}</div>
    <div class="ph-col">${ss('acct', accountPanel(dev))}${ss('sound', soundsPanel(row))}</div>
  </div>`;
}

/** Sounds, saved with the trainer: a chime when a Golden Wild shows (on unless switched off), and one on a catch. */
function soundsPanel(row: (label: string, control: string) => string) {
  const st = S.trainer?.settings, pick = st?.catchSound ?? 'off';
  const names: Record<CatchSound, string> = { off: 'Off', A: 'A grade and up', all: 'Every catch' };
  return `<section class="panel"><h4>Sounds</h4>
      ${row('Golden Wild sound', `<input type="checkbox" class="toggle" data-setting-bool="goldenSound"${st?.goldenSound !== false ? ' checked' : ''} aria-label="Chime when a Golden Wild shows">`)}
      ${row('Catch sound', `<select data-setting="catchSound" aria-label="Chime on a catch">${CATCH_SOUNDS.map((v) => `<option value="${v}"${v === pick ? ' selected' : ''}>${names[v]}</option>`).join('')}</select>`)}
    </section>`;
}

export function hubPane(admin: boolean): string {
  viewerAdmin = admin;
  const tab = tabOf();
  const body = tab === 'showcase' ? showcaseTab() : tab === 'outfits' ? outfits() : tab === 'friends' ? friendsTab() : tab === 'settings' ? settings(admin) : overview();
  // A phone shows who you are on the Overview only, as its first section (client/psec.ts).
  if (tab !== '') return `<div class="player-hub ph-tab-${tab}"><div class="ph-who">${identity()}</div><div class="ph-body">${body}</div></div>`;
  return `<div class="player-hub">${psecTabs('hub', [{ key: 'you', title: 'You' }, { key: 'life', title: 'Lifetime' }, { key: 'records', title: 'Records' }, { key: 'social', title: 'Social' }])}${psecWrap('hub', 'you', HUB_SECS, identity())}<div class="ph-body">${body}</div></div>`;
}

/** Clicks the hub owns. Returns true when handled; rerender after. */
export function hubClick(act: string, id: string, el: HTMLElement): boolean {
  if (act === 'ph-show') {
    const t = S.trainer!, n = Number(id);
    const cur = (t.showcase ?? []).filter((x) => S.creatures.has(x));
    const next = cur.includes(n) ? cur.filter((x) => x !== n) : cur.length < SOCIAL.showcaseMax ? [...cur, n] : cur;
    t.showcase = next; sendShowcase(next);
    return true;
  }
  if (act === 'ph-comp') {
    const n = Number(id) || 0, t = S.trainer!;
    if (n) t.companion = n; else delete t.companion;
    sendCompanion(n || null);
    return true;
  }
  if (act === 'ph-move') {
    const t = S.trainer!, [n, d] = id.split(':').map(Number);
    const cur = (t.showcase ?? []).filter((x) => S.creatures.has(x));
    const i = cur.indexOf(n), j = i + d;
    if (i < 0 || j < 0 || j >= cur.length) return true;
    [cur[i], cur[j]] = [cur[j], cur[i]];
    t.showcase = cur; sendShowcase(cur);
    return true;
  }
  if (act === 'ph-titles') { void pickTitle(); return false; }
  if (act === 'ph-card') {
    const t = S.trainer!;
    const next = { ...(t.card ?? {}), [id]: !(el as HTMLInputElement).checked };
    t.card = cleanCardPrefs(next);
    sendCard(t.card);
    return true;
  }
  if (act === 'ph-saver') { setBatterySaver((el as HTMLInputElement).checked); return true; }
  if (act === 'ph-wayfarers') { setShowWayfarers((el as HTMLInputElement).checked); return true; }
  if (act === 'ph-zoom') { setZoom(Number(id)); return true; }
  if (act.startsWith('ph-ac-')) return accountClick(act);
  if (act === 'ph-theme') { try { localStorage.setItem('mi-theme', id); } catch { /* storage off */ } location.reload(); return true; }
  return false;
}
